export const MAX_QUOTED_LENGTH = 800

/**
 * The wire format for a "selected text → question" chat message. The officer
 * only ever types the question; the quote travels with it so the agent has
 * the context. parseSelectionMessage() is the exact inverse, letting the chat
 * panel render the quote as a styled block instead of a wall of pasted text.
 */
const SELECTION_PREFIX = "Regarding this section of the draft:"

export function truncateSelection(selectedText: string): string {
  return selectedText.length > MAX_QUOTED_LENGTH
    ? selectedText.slice(0, MAX_QUOTED_LENGTH) + "..."
    : selectedText
}

export function buildSelectionMessage(selectedText: string, question: string): string {
  return `${SELECTION_PREFIX}\n\n"${truncateSelection(selectedText)}"\n\n${question}`
}

export interface ParsedSelectionMessage {
  quote: string
  question: string
}

/** Inverse of buildSelectionMessage; null for ordinary messages. */
export function parseSelectionMessage(content: string): ParsedSelectionMessage | null {
  if (!content.startsWith(`${SELECTION_PREFIX}\n\n"`)) return null
  const rest = content.slice(SELECTION_PREFIX.length + 3)
  const closing = rest.lastIndexOf('"\n\n')
  if (closing === -1) return null
  const quote = rest.slice(0, closing)
  const question = rest.slice(closing + 3).trim()
  if (!question) return null
  return { quote, question }
}

/**
 * Context-aware quick questions for the selection composer. Purely heuristic
 * on the selected text — amounts get computation questions, provisions get
 * legal-basis questions, case names get citation questions — topped up with
 * generic ones to always offer a few.
 */
export function suggestionsFor(selectedText: string): string[] {
  const s = selectedText
  const out: string[] = []
  if (/₹|\bRs\.?\s?\d|\bcrore\b|\blakh\b|\d+(\.\d+)?\s*%|\d{2,}(,\d{2,3})+/i.test(s)) {
    out.push("Explain how this amount was computed")
  }
  if (/\bsection\s+\d+|\brule\s+\d+|\bnotification\b|\bcircular\b|\bCGST\b|\bSGST\b|\bIGST\b/i.test(s)) {
    out.push("Explain the legal basis for this")
  }
  if (/\bv(?:s?\.)\s|\bversus\b|\bSupreme Court\b|\bHigh Court\b|\bTribunal\b|\bCESTAT\b|\bAAR\b/i.test(s)) {
    out.push("Is this authority correctly applied here?")
  }
  if (/\bpenalty\b|\binterest\b|\blate fee\b/i.test(s)) {
    out.push("Why is this penalty applicable?")
  }
  if (/\blimitation\b|\btime[- ]bar\b|\bextended period\b|\bsuppression\b/i.test(s)) {
    out.push("Is the extended period justified here?")
  }
  for (const generic of [
    "Explain this in simpler terms",
    "How does the record support this finding?",
  ]) {
    if (out.length >= 4) break
    out.push(generic)
  }
  return out.slice(0, 4)
}
